import { EditableType, EditorType } from '../../../constants';
import { editableId, editedMediaScriptAttribute, EditableItem } from '../constants';
import { HtmlMediaElementConfig, MediaElementDataAttribute, isEditedImageElement } from './getData';
import { MediaElement } from '../../editableMedia/typing';
import { getDriveLinkWithoutHash, getElement, setDataAttributes, setTooltipActions } from '../../editableMedia/saveData';

interface ImageStyleData {
	width: number;
	x: number;
	y: number;
}

interface ImageFrameStyleData {
	frameHeight: number;
	defaultStyle: string;
}

const setImageFrameStyle = (element: HTMLElement, { frameHeight, defaultStyle }: ImageFrameStyleData): void => {
	const style = `position:relative;display:inline-block;vertical-align:middle;overflow:hidden;width:100%;height:${frameHeight}px;${defaultStyle}`;
	element.setAttribute('style', style);
};

const setImageStyle = (element: MediaElement, { width, x, y }: ImageStyleData): void => {
	const style = `position:absolute;width:${width}%;height:auto;top:${y}%;left:${x}%`;
	element.setAttribute('style', style);
};

const setElementContent = (htmlDocument: HTMLDocument, element: MediaElement, id: string, type: EditableType, defaultStyle: string): void => {
	const editableTypeAttr = type === EditableType.image ? EditableItem.Image : EditableItem.Video;
	element.setAttribute(editableTypeAttr, '');
	element.setAttribute(editableId, id);

	if (defaultStyle) {
		element.setAttribute('style', defaultStyle);
	}

	const oldElement = htmlDocument.querySelector(`[${editableId}=${id}]`) as HTMLElement;
	const classes = oldElement.getAttribute('class');

	if (classes) {
		element.setAttribute('class', classes);
	}

	oldElement.parentNode!.replaceChild(element, oldElement);
};

// is needed for handling height of edited media in Reader
const EDITED_MEDIA_SCRIPTS = `
	<script src="https://polyfill.io/v3/polyfill.min.js?features=IntersectionObserver" ${editedMediaScriptAttribute}></script>
	<script ${editedMediaScriptAttribute}>
		var editedMediaSelector = '[${EditableItem.Image}][${MediaElementDataAttribute.edited}], iframe[${EditableItem.Video}][${MediaElementDataAttribute.edited}]';
		var editedMedia = document.querySelectorAll(editedMediaSelector);
		var editedMediaArray = Array.prototype.slice.call(editedMedia);
		var observer = new IntersectionObserver(handleIntersect);

		for (var i = 0; i < editedMediaArray.length; i++) {
			var editedMedia = editedMediaArray[i];
			observer.observe(editedMedia);
		}

		function handleIntersect(entries, observer) {
			for (var i = 0; i < entries.length; i++) {
				var entry = entries[i];
				if (entry.isIntersecting) {
					setMediaHeight(entry.target);
				}
			}
		};

		function handleResize() {
			for (var i = 0; i < editedMediaArray.length; i++) {
				setMediaHeight(editedMediaArray[i]);
			}
		};

		function setMediaHeight(mediaElement) {
			var defaultStyle = mediaElement.getAttribute('${MediaElementDataAttribute.defaultStyle}');

			if (defaultStyle && isStyleIncludesHeight(defaultStyle)) {
				return;
			}

			var width = mediaElement.getBoundingClientRect().width;
			var aspectRatio = mediaElement.getAttribute('${MediaElementDataAttribute.aspectRatio}');
			mediaElement.style.height = width * aspectRatio + 'px';
		};

		function isStyleIncludesHeight(style) {
			var styleWithoutSpaces = style.replace(/\\s+/g, '');
			return styleWithoutSpaces.indexOf('height') !== -1 && (
				styleWithoutSpaces.indexOf('height') === 0 ||
				styleWithoutSpaces[styleWithoutSpaces.indexOf('height') - 1] === ';'
			);
		};

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', handleResize);
		} else {
			window.addEventListener('resize', handleResize);
		}
	</script>
`;

const addOrRemoveEditedMediaScripts = (htmlDocument: HTMLDocument) => {
	const editedMediaSelector = `[${EditableItem.Image}][${MediaElementDataAttribute.edited}], iframe[${EditableItem.Video}][${MediaElementDataAttribute.edited}]`;
	const editedMedia = [...htmlDocument.querySelectorAll(editedMediaSelector)];
	const hasEditedMedia = editedMedia.length > 0;
	const editedMediaScripts = [...htmlDocument.querySelectorAll(`[${editedMediaScriptAttribute}]`)];
	const hasEditedMediaScripts = editedMediaScripts.length > 0;

	if (hasEditedMedia && !hasEditedMediaScripts) {
		htmlDocument.body.innerHTML += EDITED_MEDIA_SCRIPTS;
	}

	if (!hasEditedMedia && hasEditedMediaScripts) {
		editedMediaScripts.forEach(script => (
			script.parentNode!.removeChild(script)
		));
	}
};

export default (htmlDocument: HTMLDocument, mediaItems: HtmlMediaElementConfig[]): void => {
	mediaItems.forEach(({
		isEditedFromBackoffice,
		type,
		id,
		defaultLink,
		driveLink,
		externalLink,
		isExternalLinkActive,
		frameHeight,
		width,
		x,
		y,
		label,
		tooltip,
		isTooltipActive,
		frameAspectRatio,
		style: defaultStyle,
		videoSettings,
	}) => {
		if (!isEditedFromBackoffice) {
			return;
		}

		const modifiedDriveLink = getDriveLinkWithoutHash(driveLink);
		const src = (isExternalLinkActive ? externalLink : modifiedDriveLink) || defaultLink;
		const element = getElement(EditorType.HTML, type, isExternalLinkActive, htmlDocument, src, videoSettings);

		const isCustomLink = (src !== defaultLink) || (isExternalLinkActive && !!modifiedDriveLink);

		if (type === EditableType.image) {
			if (isCustomLink) {
				let imageFrame = htmlDocument.querySelector(`[${editableId}=${id}]`) as HTMLElement;

				if (!isEditedImageElement(imageFrame)) {
					const newImageFrame = htmlDocument.createElement('div');
					newImageFrame.setAttribute(EditableItem.Image, '');
					newImageFrame.setAttribute(editableId, id);

					const classes = imageFrame.getAttribute('class');

					if (classes) {
						newImageFrame.setAttribute('class', classes);
					}

					imageFrame.parentNode!.replaceChild(newImageFrame, imageFrame);
					imageFrame = newImageFrame;
				}

				setDataAttributes(imageFrame, {
					[MediaElementDataAttribute.edited]: true,
					[MediaElementDataAttribute.label]: label,
					[MediaElementDataAttribute.defaultLink]: defaultLink,
					[MediaElementDataAttribute.driveLink]: modifiedDriveLink,
					[MediaElementDataAttribute.externalLink]: externalLink,
					[MediaElementDataAttribute.externalLinkActive]: isExternalLinkActive,
					[MediaElementDataAttribute.defaultStyle]: defaultStyle,
					[MediaElementDataAttribute.aspectRatio]: frameAspectRatio,
					[MediaElementDataAttribute.tooltip]: tooltip,
					[MediaElementDataAttribute.tooltipActive]: isTooltipActive,
				});
				setTooltipActions(imageFrame, tooltip, isTooltipActive);
				setImageFrameStyle(imageFrame, { frameHeight, defaultStyle });
				setImageStyle(element, { width, x, y });

				imageFrame.innerHTML = '';
				imageFrame.appendChild(element);
			} else {
				setElementContent(htmlDocument, element, id, type, defaultStyle);
			}
		} else {
			if (isCustomLink) {
				setDataAttributes(element, {
					[MediaElementDataAttribute.edited]: true,
					[MediaElementDataAttribute.label]: label,
					[MediaElementDataAttribute.defaultLink]: defaultLink,
					[MediaElementDataAttribute.driveLink]: modifiedDriveLink,
					[MediaElementDataAttribute.externalLink]: externalLink,
					[MediaElementDataAttribute.externalLinkActive]: isExternalLinkActive,
					[MediaElementDataAttribute.defaultStyle]: defaultStyle,
					[MediaElementDataAttribute.aspectRatio]: frameAspectRatio,
					[MediaElementDataAttribute.tooltip]: tooltip,
					[MediaElementDataAttribute.tooltipActive]: isTooltipActive,
				});
				setTooltipActions(element, tooltip, isTooltipActive);
			}

			setElementContent(htmlDocument, element, id, type, defaultStyle);
		}
	});

	addOrRemoveEditedMediaScripts(htmlDocument);
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/htmlEditor/editableMedia/saveData.ts